import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Thead,
  Th,
  Tr,
  Table,
  Td,
  Tbody,
  Flex,
  Badge,
  TableContainer,
  StatGroup,
  StatArrow,
  StatHelpText,
  StatNumber,
  Stat,
  StatLabel,
  Spinner,
  Button,
  Link as ChakraLink,
  useDisclosure,
  Tooltip,
  Switch,
  FormControl,
  FormLabel,
  Icon,
} from '@chakra-ui/react';
import { SortAscending, SortDescending, Equals, ArrowsClockwise, Question } from '@phosphor-icons/react';
import { useContext, useState, useEffect } from 'react';
import moment from 'moment';

import ConsigneeScanModal from './ConsigneeScanModal';
import AgedParcelScanModal from './AgedParcelScanModal';
import { formatUTCToLocal } from '../../../utils';
import {
  amazonManualEntryTooltip,
  amazonParcelStatusText,
  amazonStatusColorScheme,
  canAgedParcelScan,
  canConsigneePickup,
  hubCounterLabel,
} from './amazonParcelActions';
import {
  GET_AMAZON_PARCELS,
  GET_AMAZON_PARCEL_STATISTICS,
  InternalAllAmazonParcelsQuery,
  InternalAmazonParcelStatisticsQuery,
  AmazonDateFilters,
  AmazonParcelSortField,
  AMAZON_PARCEL_STATUSES,
  SET_AMAZON_PARCEL_HIDDEN_IN_APP,
  RESTORE_AMAZON_PARCEL_SIBLING,
} from './query';
import { Pagination } from '../../../components/Pagination';
import Layout from '../../../components/Layout';
import { FilterSelectPill } from '../../../components/FilterPill/Select';
import { FilterTextInputPill } from '../../../components/FilterPill/TextInput';
import { FilterDateRangePill } from '../../../components/FilterPill/DateRange';
import UserContext from '../../../contexts/UserContext';
import { useCustomToast } from '../../../hooks/useCustomToast';

const getUrlParams = () => new URLSearchParams(window.location.search);

const getArrayFromUrl = (params: URLSearchParams, key: string): string[] => {
  const value = params.get(key);
  return value ? value.split(',').filter(Boolean) : [];
};

const getDateRangeFromUrl = (params: URLSearchParams, prefix: string) => {
  return {
    startDate: params.get(`${prefix}From`) || '',
    endDate: params.get(`${prefix}To`) || '',
  };
};

const POLLING_INTERVAL = 10000;

export type AmazonParcelTableProps = {
  lockedAmazonAccessPointIds?: string[];
  showStats?: boolean;
  showHubCounterFilter?: boolean;
  syncUrl?: boolean;
  embedded?: boolean;
};

export const AmazonParcelTable = ({
  lockedAmazonAccessPointIds,
  showStats = true,
  showHubCounterFilter = true,
  syncUrl = true,
  embedded = false,
}: AmazonParcelTableProps) => {
  const limit = 50;
  const { user } = useContext(UserContext);
  const toast = useCustomToast();
  const [setParcelHiddenInApp] = useMutation(SET_AMAZON_PARCEL_HIDDEN_IN_APP);
  const [restoreParcelSibling] = useMutation(RESTORE_AMAZON_PARCEL_SIBLING);
  const { isOpen: isConsigneeScanOpen, onOpen: onConsigneeScanOpen, onClose: onConsigneeScanClose } = useDisclosure();
  const { isOpen: isAgedParcelScanOpen, onOpen: onAgedParcelScanOpen, onClose: onAgedParcelScanClose } = useDisclosure();
  const [selectedParcel, setSelectedParcel] = useState<{
    trackingNumber: string;
    amazonAccessPointId: string;
  } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);

  const params = getUrlParams();
  const [offset, setOffset] = useState(syncUrl ? parseInt(params.get('offset') || '0', 10) : 0);
  const [statuses, setStatuses] = useState(syncUrl ? getArrayFromUrl(params, 'statuses') : []);
  const [hubCounterIds, setHubCounterIds] = useState(syncUrl ? getArrayFromUrl(params, 'hubCounterIds') : []);
  const [trackingNumberSearch, setTrackingNumberSearch] = useState(syncUrl ? params.get('trackingNumber') || '' : '');
  const [sortBy, setSortBy] = useState<AmazonParcelSortField>(
    (syncUrl ? (params.get('sortBy') as AmazonParcelSortField) : undefined) || 'createdAt'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((syncUrl ? (params.get('sortOrder') as 'asc' | 'desc') : undefined) || 'desc');
  const [createdAtRange, setCreatedAtRange] = useState(syncUrl ? getDateRangeFromUrl(params, 'createdAt') : { startDate: '', endDate: '' });
  const [updatedAtRange, setUpdatedAtRange] = useState(syncUrl ? getDateRangeFromUrl(params, 'updatedAt') : { startDate: '', endDate: '' });
  const [expectedArrivalAtRange, setExpectedArrivalAtRange] = useState(
    syncUrl ? getDateRangeFromUrl(params, 'expectedArrivalAt') : { startDate: '', endDate: '' }
  );
  const [driverDropOffAtRange, setDriverDropOffAtRange] = useState(
    syncUrl ? getDateRangeFromUrl(params, 'driverDropOffAt') : { startDate: '', endDate: '' }
  );
  const [consigneePickupAtRange, setConsigneePickupAtRange] = useState(
    syncUrl ? getDateRangeFromUrl(params, 'consigneePickupAt') : { startDate: '', endDate: '' }
  );
  const [parcelAgedAtRange, setParcelAgedAtRange] = useState(
    syncUrl ? getDateRangeFromUrl(params, 'parcelAgedAt') : { startDate: '', endDate: '' }
  );
  const [dispossessedAtRange, setDispossessedAtRange] = useState(
    syncUrl ? getDateRangeFromUrl(params, 'dispossessedAt') : { startDate: '', endDate: '' }
  );
  const [scanEventFilters, setScanEventFilters] = useState<string[]>(syncUrl ? getArrayFromUrl(params, 'scanEvents') : []);

  useEffect(() => {
    if (!syncUrl) return;
    const nextParams = new URLSearchParams();
    if (offset > 0) nextParams.set('offset', offset.toString());
    if (trackingNumberSearch) nextParams.set('trackingNumber', trackingNumberSearch);
    if (statuses.length > 0) nextParams.set('statuses', statuses.join(','));
    if (hubCounterIds.length > 0) nextParams.set('hubCounterIds', hubCounterIds.join(','));
    if (scanEventFilters.length > 0) nextParams.set('scanEvents', scanEventFilters.join(','));
    if (sortBy !== 'createdAt') nextParams.set('sortBy', sortBy);
    if (sortOrder !== 'desc') nextParams.set('sortOrder', sortOrder);
    if (createdAtRange.startDate) nextParams.set('createdAtFrom', createdAtRange.startDate);
    if (createdAtRange.endDate) nextParams.set('createdAtTo', createdAtRange.endDate);
    if (updatedAtRange.startDate) nextParams.set('updatedAtFrom', updatedAtRange.startDate);
    if (updatedAtRange.endDate) nextParams.set('updatedAtTo', updatedAtRange.endDate);
    if (expectedArrivalAtRange.startDate) nextParams.set('expectedArrivalAtFrom', expectedArrivalAtRange.startDate);
    if (expectedArrivalAtRange.endDate) nextParams.set('expectedArrivalAtTo', expectedArrivalAtRange.endDate);
    if (driverDropOffAtRange.startDate) nextParams.set('driverDropOffAtFrom', driverDropOffAtRange.startDate);
    if (driverDropOffAtRange.endDate) nextParams.set('driverDropOffAtTo', driverDropOffAtRange.endDate);
    if (consigneePickupAtRange.startDate) nextParams.set('consigneePickupAtFrom', consigneePickupAtRange.startDate);
    if (consigneePickupAtRange.endDate) nextParams.set('consigneePickupAtTo', consigneePickupAtRange.endDate);
    if (parcelAgedAtRange.startDate) nextParams.set('parcelAgedAtFrom', parcelAgedAtRange.startDate);
    if (parcelAgedAtRange.endDate) nextParams.set('parcelAgedAtTo', parcelAgedAtRange.endDate);
    if (dispossessedAtRange.startDate) nextParams.set('dispossessedAtFrom', dispossessedAtRange.startDate);
    if (dispossessedAtRange.endDate) nextParams.set('dispossessedAtTo', dispossessedAtRange.endDate);
    const newUrl = `${window.location.pathname}${nextParams.toString() ? '?' + nextParams.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [
    syncUrl,
    offset,
    trackingNumberSearch,
    statuses,
    hubCounterIds,
    scanEventFilters,
    sortBy,
    sortOrder,
    createdAtRange,
    updatedAtRange,
    expectedArrivalAtRange,
    driverDropOffAtRange,
    consigneePickupAtRange,
    parcelAgedAtRange,
    dispossessedAtRange,
  ]);

  const buildDateFilters = (): AmazonDateFilters | undefined => {
    const filters: AmazonDateFilters = {};
    if (createdAtRange.startDate) filters.createdAtFrom = createdAtRange.startDate;
    if (createdAtRange.endDate) filters.createdAtTo = createdAtRange.endDate;
    if (updatedAtRange.startDate) filters.updatedAtFrom = updatedAtRange.startDate;
    if (updatedAtRange.endDate) filters.updatedAtTo = updatedAtRange.endDate;
    if (expectedArrivalAtRange.startDate) filters.expectedArrivalAtFrom = expectedArrivalAtRange.startDate;
    if (expectedArrivalAtRange.endDate) filters.expectedArrivalAtTo = expectedArrivalAtRange.endDate;
    if (driverDropOffAtRange.startDate) filters.driverDropOffAtFrom = driverDropOffAtRange.startDate;
    if (driverDropOffAtRange.endDate) filters.driverDropOffAtTo = driverDropOffAtRange.endDate;
    if (consigneePickupAtRange.startDate) filters.consigneePickupAtFrom = consigneePickupAtRange.startDate;
    if (consigneePickupAtRange.endDate) filters.consigneePickupAtTo = consigneePickupAtRange.endDate;
    if (parcelAgedAtRange.startDate) filters.parcelAgedAtFrom = parcelAgedAtRange.startDate;
    if (parcelAgedAtRange.endDate) filters.parcelAgedAtTo = parcelAgedAtRange.endDate;
    if (dispossessedAtRange.startDate) filters.dispossessedAtFrom = dispossessedAtRange.startDate;
    if (dispossessedAtRange.endDate) filters.dispossessedAtTo = dispossessedAtRange.endDate;
    if (scanEventFilters.includes('hasDriverDropOff')) filters.hasDriverDropOff = true;
    if (scanEventFilters.includes('hasConsigneePickup')) filters.hasConsigneePickup = true;
    if (scanEventFilters.includes('hasParcelAged')) filters.hasParcelAged = true;
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const amazonAccessPointIds = lockedAmazonAccessPointIds?.length ? lockedAmazonAccessPointIds : hubCounterIds;
  const skipQuery = Boolean(lockedAmazonAccessPointIds && lockedAmazonAccessPointIds.length === 0);

  const { data, loading, refetch } = useQuery<InternalAllAmazonParcelsQuery>(GET_AMAZON_PARCELS, {
    skip: skipQuery,
    variables: {
      limit,
      offset,
      sortBy,
      sortOrder,
      statuses,
      amazonAccessPointIds: amazonAccessPointIds.length > 0 ? amazonAccessPointIds : undefined,
      trackingNumberSearch: trackingNumberSearch || undefined,
      dateFilters: buildDateFilters(),
      includeSuperseded,
    },
    pollInterval: autoRefresh && !skipQuery ? POLLING_INTERVAL : 0,
  });

  const { data: statisticsData, loading: statisticsLoading } = useQuery<InternalAmazonParcelStatisticsQuery>(GET_AMAZON_PARCEL_STATISTICS, {
    skip: !showStats,
    variables: {
      amazonAccessPointIds: amazonAccessPointIds.length > 0 ? amazonAccessPointIds : undefined,
    },
  });

  const handleLoadMore = (page: number) => {
    setOffset((page - 1) * limit);
  };

  const handleSort = (field: AmazonParcelSortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setOffset(0);
  };

  const renderSortIcon = (field: AmazonParcelSortField) => {
    if (sortBy !== field) {
      return <Equals size={16} style={{ display: 'inline', marginLeft: '0.25rem', opacity: 0.4, verticalAlign: 'middle' }} />;
    }
    return sortOrder === 'asc' ? (
      <SortAscending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    ) : (
      <SortDescending size={16} style={{ display: 'inline', marginLeft: '0.25rem', verticalAlign: 'middle' }} />
    );
  };

  const getCountByStatus = (status: string) => {
    return statisticsData?.internalAmazonParcelStatistics.byStatus.find((item) => item.status === status)?.count || 0;
  };

  const thisMonth = statisticsData?.internalAmazonParcelStatistics.historicalData.find(
    (item) => item.period === moment().format('YYYY-MM')
  );
  const lastMonth = statisticsData?.internalAmazonParcelStatistics.historicalData.find(
    (item) => item.period === moment().subtract(1, 'month').format('YYYY-MM')
  );
  const lastMonthCount = lastMonth?.count || 0;
  const thisMonthCount = thisMonth?.count || 0;

  const content = (
    <>
      {showStats ? (
        <Box mb="4">
          <StatGroup>
            <Stat>
              <StatLabel>Aktive Hub Counter</StatLabel>
              {statisticsLoading ? (
                <Spinner />
              ) : (
                <>
                  <StatNumber>{statisticsData?.internalAmazonParcelStatistics.activeHubCounters}</StatNumber>
                  <StatHelpText>({statisticsData?.internalAmazonParcelStatistics.totalHubCounters} insgesamt)</StatHelpText>
                </>
              )}
            </Stat>
            <Stat>
              <StatLabel>Pakete {moment().format('MMM YYYY')}</StatLabel>
              {statisticsLoading ? (
                <Spinner />
              ) : (
                <>
                  <StatNumber>{thisMonthCount}</StatNumber>
                  <StatHelpText>
                    <StatArrow type={thisMonthCount > lastMonthCount ? 'increase' : 'decrease'} />
                    {Math.round(lastMonthCount ? ((thisMonthCount - lastMonthCount) / lastMonthCount) * 100 : 0)}%
                  </StatHelpText>
                </>
              )}
            </Stat>
            <Stat>
              <StatLabel>Pakete {moment().subtract(1, 'month').format('MMM YYYY')}</StatLabel>
              {statisticsLoading ? <Spinner /> : <StatNumber>{lastMonthCount}</StatNumber>}
            </Stat>
            <Stat>
              <StatLabel>Pakete INBOUND</StatLabel>
              {statisticsLoading ? <Spinner /> : <StatNumber>{getCountByStatus('INBOUND')}</StatNumber>}
            </Stat>
            <Stat>
              <StatLabel>Pakete im Inventar</StatLabel>
              {statisticsLoading ? <Spinner /> : <StatNumber>{getCountByStatus('READY_FOR_CONSIGNEE_PICKUP')}</StatNumber>}
            </Stat>
          </StatGroup>
          <StatGroup>
            <Stat>
              <StatLabel>DISPOSSESSIONS {moment().format('MMM YYYY')}</StatLabel>
              {statisticsLoading ? (
                <Spinner />
              ) : (
                <>
                  <StatNumber>{statisticsData?.internalAmazonParcelStatistics.dispossessionsThisMonth}</StatNumber>
                  <StatHelpText>
                    <StatArrow
                      type={
                        (statisticsData?.internalAmazonParcelStatistics.dispossessionsLastMonth || 0) <
                        (statisticsData?.internalAmazonParcelStatistics.dispossessionsThisMonth || 0)
                          ? 'increase'
                          : 'decrease'
                      }
                    />
                    {Math.round(
                      statisticsData?.internalAmazonParcelStatistics.dispossessionsThisMonth
                        ? ((statisticsData?.internalAmazonParcelStatistics.dispossessionsThisMonth -
                            (statisticsData?.internalAmazonParcelStatistics.dispossessionsLastMonth || 0)) /
                            (statisticsData?.internalAmazonParcelStatistics.dispossessionsLastMonth || 1)) *
                            100
                        : 0
                    )}
                    %
                  </StatHelpText>
                </>
              )}
            </Stat>
            <Stat>
              <StatLabel>DISPOSSESSIONS {moment().subtract(1, 'month').format('MMM YYYY')}</StatLabel>
              {statisticsLoading ? (
                <Spinner />
              ) : (
                <StatNumber>{statisticsData?.internalAmazonParcelStatistics.dispossessionsLastMonth}</StatNumber>
              )}
            </Stat>
          </StatGroup>
        </Box>
      ) : null}

      <Flex justifyContent="flex-start" alignItems="center" gap="2" mb="2" flexWrap="wrap">
        <FilterTextInputPill
          label="Trackingnummer"
          placeholder="Trackingnummer eingeben..."
          initialValue={trackingNumberSearch}
          setValue={(value) => {
            setTrackingNumberSearch(value);
            setOffset(0);
          }}
          clear={() => {
            setTrackingNumberSearch('');
            setOffset(0);
          }}
        />
        {showHubCounterFilter ? (
          <FilterTextInputPill
            label="Hub Counters"
            placeholder="DRF-0001, Store-ID..."
            initialValue={hubCounterIds.join(', ')}
            setValue={(value) => {
              const ids = value
                .split(',')
                .map((id) => id.trim())
                .filter(Boolean);
              setHubCounterIds(ids);
              setOffset(0);
            }}
            clear={() => {
              setHubCounterIds([]);
              setOffset(0);
            }}
          />
        ) : null}
        <FilterSelectPill
          label="Status"
          options={(
            statisticsData?.internalAmazonParcelStatistics.byStatus || AMAZON_PARCEL_STATUSES.map((status) => ({ status, count: 0 }))
          )
            .filter((item) => AMAZON_PARCEL_STATUSES.includes(item.status))
            .map((item) => ({
              label: `${amazonParcelStatusText(item.status)} (${getCountByStatus(item.status)})`,
              value: item.status,
            }))}
          initialValues={statuses}
          setValues={(values) => {
            setStatuses(values);
            setOffset(0);
          }}
          clear={() => {
            setStatuses([]);
            setOffset(0);
          }}
        />
      </Flex>

      <Flex justifyContent="flex-start" alignItems="center" gap="2" mb="4" flexWrap="wrap">
        <FilterDateRangePill
          label="Erstellt am"
          initialValues={createdAtRange}
          setValues={(values) => {
            setCreatedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setCreatedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Aktualisiert am"
          initialValues={updatedAtRange}
          setValues={(values) => {
            setUpdatedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setUpdatedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Expected Arrival"
          initialValues={expectedArrivalAtRange}
          setValues={(values) => {
            setExpectedArrivalAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setExpectedArrivalAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Fahrer Drop-Off"
          initialValues={driverDropOffAtRange}
          setValues={(values) => {
            setDriverDropOffAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setDriverDropOffAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Empfänger Abholung"
          initialValues={consigneePickupAtRange}
          setValues={(values) => {
            setConsigneePickupAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setConsigneePickupAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Paket aged"
          initialValues={parcelAgedAtRange}
          setValues={(values) => {
            setParcelAgedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setParcelAgedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterDateRangePill
          label="Dispossessed at"
          initialValues={dispossessedAtRange}
          setValues={(values) => {
            setDispossessedAtRange(values);
            setOffset(0);
          }}
          clear={() => {
            setDispossessedAtRange({ startDate: '', endDate: '' });
            setOffset(0);
          }}
        />
        <FilterSelectPill
          label="Scan Events"
          options={[
            { label: 'Hat Fahrer Drop-Off', value: 'hasDriverDropOff' },
            { label: 'Hat Empfänger Abholung', value: 'hasConsigneePickup' },
            { label: 'Hat Paket gealtert', value: 'hasParcelAged' },
          ]}
          initialValues={scanEventFilters}
          setValues={(values) => {
            setScanEventFilters(values);
            setOffset(0);
          }}
          clear={() => {
            setScanEventFilters([]);
            setOffset(0);
          }}
        />
      </Flex>

      <Flex justifyContent="flex-end" alignItems="center" gap="4" mb="2">
        <FormControl display="flex" alignItems="center" width="auto">
          <Switch
            id="amazon-include-superseded"
            isChecked={includeSuperseded}
            onChange={(e) => {
              setIncludeSuperseded(e.target.checked);
              setOffset(0);
            }}
            colorScheme="blue"
          />
          <FormLabel htmlFor="amazon-include-superseded" mb="0" fontSize="sm" ml="2">
            Ersetzte Pakete anzeigen
          </FormLabel>
        </FormControl>
        <FormControl display="flex" alignItems="center" width="auto">
          <Switch id="amazon-auto-refresh" isChecked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} colorScheme="blue" />
          <FormLabel htmlFor="amazon-auto-refresh" mb="0" fontSize="sm" ml="2">
            Auto-Refresh (10s)
          </FormLabel>
        </FormControl>
        <Tooltip label="Tabelle aktualisieren" hasArrow>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<ArrowsClockwise size={16} />}
            onClick={() => {
              if (!skipQuery) refetch();
            }}
            isLoading={loading}
            isDisabled={skipQuery}
          >
            Aktualisieren
          </Button>
        </Tooltip>
      </Flex>

      <Box>
        <TableContainer>
          <Table variant="striped" size="sm">
            <Thead>
              <Tr>
                <Th>Tracking Number</Th>
                <Th>Status & Infos</Th>
                <Th>Hub Counter</Th>
                <Th>Addressat</Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('createdAt')}>
                  <Flex alignItems="center" gap="2">
                    <span>1. Sync am</span>
                    {renderSortIcon('createdAt')}
                  </Flex>
                </Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('updatedAt')}>
                  <Flex alignItems="center" gap="2">
                    <span>Last Update</span>
                    {renderSortIcon('updatedAt')}
                  </Flex>
                </Th>
                <Th>Expected at</Th>
                <Th>Driver Drop-Off</Th>
                <Th>Consignee Pickup</Th>
                <Th>Parcel Aged</Th>
                <Th>Aged Parcel</Th>
                <Th cursor="pointer" userSelect="none" onClick={() => handleSort('dispossessedAt')}>
                  <Flex alignItems="center" gap="2">
                    <span>Dispossessed At</span>
                    {renderSortIcon('dispossessedAt')}
                  </Flex>
                </Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            {loading || skipQuery ? null : (
              <Tbody>
                {data?.internalAllAmazonParcels.parcels.map((parcel) => {
                  const manualEntryLabel = amazonManualEntryTooltip(parcel);
                  return (
                    <Tr key={`${parcel.amazonAccessPointId}-${parcel.amazonPackageId || parcel.trackingNumber}`}>
                      <Td>{parcel.trackingNumber}</Td>
                      <Td>
                        <Box>
                          <Flex flexWrap="wrap" gap="2" alignItems="center">
                            <Tooltip label={parcel.deliveryStatus} hasArrow>
                              <Badge colorScheme={amazonStatusColorScheme[parcel.deliveryStatus] || 'gray'}>
                                {amazonParcelStatusText(parcel.deliveryStatus)}
                              </Badge>
                            </Tooltip>
                            {manualEntryLabel ? (
                              <Tooltip label={manualEntryLabel} hasArrow>
                                <Box height="16px" width="16px">
                                  <Icon as={Question} />
                                </Box>
                              </Tooltip>
                            ) : null}
                            {parcel.notInInventorySync && parcel.deliveryStatus !== 'DISPOSSESSIONS' ? (
                              <Badge colorScheme="red">Nicht mehr im Inventar</Badge>
                            ) : null}
                            {parcel.hiddenInApp ? <Badge colorScheme="orange">Ausgeblendet</Badge> : null}
                          </Flex>
                          {parcel.supersededAt ? (
                            <Tooltip
                              label={
                                parcel.replacedByAmazonPackageId
                                  ? `Ersetzt durch Paket ${parcel.replacedByAmazonPackageId}`
                                  : 'Dieses Paket wurde durch ein anderes ersetzt'
                              }
                              hasArrow
                            >
                              <Box fontSize="xs">Ersetzt</Box>
                            </Tooltip>
                          ) : null}
                          {parcel.disappearedAt ? <Box fontSize="xs">Verschwunden am {formatUTCToLocal(parcel.disappearedAt)}</Box> : null}
                        </Box>
                      </Td>
                      <Td>
                        {parcel.userId ? (
                          <ChakraLink href={`/users/${parcel.userId}`} target="_blank" rel="noopener noreferrer" color="blue.500">
                            {hubCounterLabel(parcel)}
                          </ChakraLink>
                        ) : (
                          hubCounterLabel(parcel)
                        )}
                      </Td>
                      <Td>{parcel.consigneeName}</Td>
                      <Td>{formatUTCToLocal(parcel.createdAt)}</Td>
                      <Td>{formatUTCToLocal(parcel.updatedAt)}</Td>
                      <Td>{formatUTCToLocal(parcel.expectedArrivalAt) || '-'}</Td>
                      <Td>
                        {!parcel.driverDropOffAt && !parcel.driverDropOffAckAt ? (
                          '-'
                        ) : (
                          <Box fontSize="xs">
                            <Box>Scan At: {formatUTCToLocal(parcel.driverDropOffAt) || '-'}</Box>
                            <Box>Success: {formatUTCToLocal(parcel.driverDropOffAckAt) || '-'}</Box>
                          </Box>
                        )}
                      </Td>
                      <Td>
                        {!parcel.consigneePickupAt && !parcel.consigneePickupAckAt ? (
                          '-'
                        ) : (
                          <Box fontSize="xs">
                            <Box>Scan At: {formatUTCToLocal(parcel.consigneePickupAt) || '-'}</Box>
                            <Box>Success: {formatUTCToLocal(parcel.consigneePickupAckAt) || '-'}</Box>
                          </Box>
                        )}
                      </Td>
                      <Td>{formatUTCToLocal(parcel.parcelAgedAt) || '-'}</Td>
                      <Td>
                        {!parcel.agedParcelScanAt && !parcel.agedParcelScanAckAt ? (
                          '-'
                        ) : (
                          <Box fontSize="xs">
                            <Box>Scan At: {formatUTCToLocal(parcel.agedParcelScanAt) || '-'}</Box>
                            <Box>Success: {formatUTCToLocal(parcel.agedParcelScanAckAt) || '-'}</Box>
                          </Box>
                        )}
                      </Td>
                      <Td>{formatUTCToLocal(parcel.dispossessedAt) || '-'}</Td>
                      <Td>
                        <Flex gap="1" flexWrap="wrap">
                          {canConsigneePickup(parcel) ? (
                            <Tooltip label="Paket übergeben" hasArrow>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => {
                                  setSelectedParcel({
                                    trackingNumber: parcel.trackingNumber,
                                    amazonAccessPointId: parcel.amazonAccessPointId,
                                  });
                                  onConsigneeScanOpen();
                                }}
                              >
                                Übergeben
                              </Button>
                            </Tooltip>
                          ) : null}
                          {canAgedParcelScan(parcel) ? (
                            <Tooltip label="Simuliert den Scan für ein gealtertes Paket" hasArrow>
                              <Button
                                size="xs"
                                colorScheme="orange"
                                variant="outline"
                                onClick={() => {
                                  setSelectedParcel({
                                    trackingNumber: parcel.trackingNumber,
                                    amazonAccessPointId: parcel.amazonAccessPointId,
                                  });
                                  onAgedParcelScanOpen();
                                }}
                              >
                                Aged Parcel
                              </Button>
                            </Tooltip>
                          ) : null}
                          <Tooltip label={parcel.hiddenInApp ? 'Paket in der App einblenden' : 'Paket in der App ausblenden'} hasArrow>
                            <Button
                              size="xs"
                              colorScheme={parcel.hiddenInApp ? 'green' : 'orange'}
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await setParcelHiddenInApp({
                                    variables: {
                                      amazonAccessPointId: parcel.amazonAccessPointId,
                                      trackingNumber: parcel.trackingNumber,
                                      hidden: !parcel.hiddenInApp,
                                      internalUserId: user.id,
                                    },
                                  });
                                  toast({
                                    title: 'Erfolg!',
                                    description: parcel.hiddenInApp ? 'Paket wurde eingeblendet.' : 'Paket wurde ausgeblendet.',
                                    status: 'success',
                                  });
                                  refetch();
                                } catch (error) {
                                  toast({
                                    description: 'Ein Fehler ist aufgetreten!',
                                    status: 'error',
                                  });
                                }
                              }}
                            >
                              {parcel.hiddenInApp ? 'Einblenden' : 'Ausblenden'}
                            </Button>
                          </Tooltip>
                          {parcel.supersededAt && parcel.amazonPackageId ? (
                            <Tooltip label="Falsche Ersetzung rückgängig machen" hasArrow>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                variant="outline"
                                onClick={async () => {
                                  try {
                                    await restoreParcelSibling({
                                      variables: {
                                        amazonAccessPointId: parcel.amazonAccessPointId,
                                        amazonPackageId: parcel.amazonPackageId,
                                        internalUserId: user.id,
                                      },
                                    });
                                    toast({
                                      title: 'Erfolg!',
                                      description: 'Paket wurde wiederhergestellt.',
                                      status: 'success',
                                    });
                                    refetch();
                                  } catch (error) {
                                    toast({
                                      description: 'Ein Fehler ist aufgetreten!',
                                      status: 'error',
                                    });
                                  }
                                }}
                              >
                                Wiederherstellen
                              </Button>
                            </Tooltip>
                          ) : null}
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            )}
          </Table>
        </TableContainer>
        {loading ? (
          <Flex width="100%" height="100%" justifyContent="center" alignItems="center" p="12">
            <Spinner />
          </Flex>
        ) : null}
        <Box>
          <Pagination
            totalAmount={data?.internalAllAmazonParcels.totalCount || 0}
            amountPerPage={limit}
            currentPage={offset / limit + 1}
            handleOnClick={handleLoadMore}
          />
        </Box>
      </Box>

      {selectedParcel ? (
        <ConsigneeScanModal
          isOpen={isConsigneeScanOpen}
          onClose={() => {
            onConsigneeScanClose();
            setSelectedParcel(null);
          }}
          refetch={refetch}
          trackingNumber={selectedParcel.trackingNumber}
          amazonAccessPointId={selectedParcel.amazonAccessPointId}
        />
      ) : null}

      {selectedParcel ? (
        <AgedParcelScanModal
          isOpen={isAgedParcelScanOpen}
          onClose={() => {
            onAgedParcelScanClose();
            setSelectedParcel(null);
          }}
          refetch={refetch}
          trackingNumber={selectedParcel.trackingNumber}
          amazonAccessPointId={selectedParcel.amazonAccessPointId}
        />
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <Box borderLeftWidth="1px" borderRightWidth="1px" borderBottomWidth="1px" px="4" py="8">
        {content}
      </Box>
    );
  }

  return <Layout fullWidth>{content}</Layout>;
};
